import { type Scope } from "~/hooks/useScope";
import { useCanGetSpace } from "~/lib/iam/actions/mondoo/captain/Captain/GetSpace";
import { useCanListSpaceMemberships } from "~/lib/iam/actions/mondoo/captain/Captain/ListSpaceMemberships";
import { useCanListWifAuthBindings } from "~/lib/iam/actions/mondoo/captain/Captain/ListWIFAuthBindings";
import { useCanList as useCanListIntegrations } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/List";
import { useCanGenerateRegistrationToken } from "~/lib/iam/actions/mondoo/agents/AgentManager/GenerateRegistrationToken";
import { useCanListRegistrationTokens } from "~/lib/iam/actions/mondoo/agents/AgentManager/ListRegistrationTokens";
import { useCanListApiTokens } from "~/lib/iam/actions/mondoo/agents/AgentManager/ListAPITokens";
import { useCanListServiceAccounts } from "~/lib/iam/actions/mondoo/agents/AgentManager/ListServiceAccounts";
import { useCanListLogs } from "~/lib/iam/actions/mondoo/auditlog/AuditLogManager/ListLogs";

/**
 * Permission checks for the space settings tabs, matching the checks used by
 * useScopeNavigation in the new UI. Used both for gating individual tabs
 * (SpaceSettings) and for showing the Settings nav entry (SpaceRoutes), so
 * the two cannot drift apart.
 */
export function useSpaceSettingsPermissions(scope?: Scope) {
  const canGetSpace = useCanGetSpace(scope);
  const canListMembers = useCanListSpaceMemberships(scope);
  const canListIntegrations = useCanListIntegrations(scope);
  const canListRegistrationTokens = useCanListRegistrationTokens(scope);
  const canGenerateRegistrationToken = useCanGenerateRegistrationToken(scope);
  // The new UI gates Registration Tokens on the (write) generate permission;
  // also accept the list permission so read-only users can view the tab.
  const canViewRegistrationTokens =
    canListRegistrationTokens || canGenerateRegistrationToken;
  const canListApiTokens = useCanListApiTokens(scope);
  const canListServiceAccounts = useCanListServiceAccounts(scope);
  const canListWifAuthBindings = useCanListWifAuthBindings(scope);
  const canListLogs = useCanListLogs(scope);

  // Settings is visible if the user can view any of its tabs, matching
  // canViewSpaceSettings in the new UI's useScopeNavigation.
  const canViewSpaceSettings = [
    canGetSpace,
    canListApiTokens,
    canListLogs,
    canListMembers,
    canListServiceAccounts,
    canViewRegistrationTokens,
    canListWifAuthBindings,
    canListIntegrations,
  ].some(Boolean);

  return {
    canGetSpace,
    canListMembers,
    canListIntegrations,
    canViewRegistrationTokens,
    canListApiTokens,
    canListServiceAccounts,
    canListWifAuthBindings,
    canListLogs,
    canViewSpaceSettings,
  };
}
